import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useLazyQuery, useApolloClient } from "@apollo/client";
import { Box } from "@mui/material";
import { Search } from "lucide-react";
import {
  GET_QUESTIONS_UNIQUE_RESPONSE,
  CURRENT_USER_PROFIILE,
  GET_USER_FORMS,
  GET_USER_FORM,
  GET_HEADQUARTERS,
  ADD_UPDATE_COMPANY_DETAILS,
} from "../../services/userApi";
import CompanyCard from "./CompanyCard";
import BackButton from "../../reusableField/BackButton";

const Companies = () => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const [companyQuestionId, setCompanyQuestionId] = useState(null);
  const [hqQuestionId, setHqQuestionId] = useState(null);
  const [formDetails, setFormDetails] = useState({ formId: null, formUuid: null });
  const [hqMap, setHqMap] = useState({}); // { [companyName]: headquartersString }
  const sessionleadid = parseInt(sessionStorage.getItem("dashboardRoleId"));

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const limit = 10;
  const [pagingCriteria, setPagingCriteria] = useState({
    limit: limit,
    page: currentPage,
    sortByField: "created_at",
    sortOrder: "desc",
  });

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm !== undefined) {
      setCurrentPage(1);
      setPagingCriteria(prev => ({ ...prev, page: 1 }));
    }
  }, [debouncedSearchTerm]);

  // Get current user profile
  const { data: userData } = useQuery(CURRENT_USER_PROFIILE);
  const roleId = userData?.currentUserProfile?.roleId;
  const accountId = parseInt(sessionStorage.getItem("accountId"));
  const userId = parseInt(sessionStorage.getItem("userId"));

  // Get user forms
  const {
    data: userForms,
    loading: userFormsLoading,
  } = useQuery(GET_USER_FORMS, {
    variables: { roleId: sessionleadid || roleId },
    // skip: !roleId || !sessionleadid,
    fetchPolicy: "cache-and-network",
  });

  // Lazy query for getting user form details
  const [getUserForm, { data: userFormData, loading: userFormLoading }] = useLazyQuery(
    GET_USER_FORM,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        // Find the question with questionType 'COMPANYFORMFIELD'
        const questions = data?.getUserForm?.questions || [];
        const companyQuestion = questions.find(
          (q) => q.questionType === "COMPANYFORMFIELD"
        );
        const hqQuestion = questions.find(
          (q) => q.questionType === "COMPANYHEADQUARTER"
        );

        if (companyQuestion) {
          setCompanyQuestionId(companyQuestion.id);
        }
        if (hqQuestion) {
          setHqQuestionId(hqQuestion.id);
        }
      },
    }
  );

  // Get companies data
  const {
    data: companiesData,
    loading: companiesLoading,
    error: companiesError,
    refetch: refetchCompanies,
  } = useQuery(GET_QUESTIONS_UNIQUE_RESPONSE, {
    variables: {
      jobroleId: sessionleadid || roleId,
      questionId: companyQuestionId,
      searchTerm: debouncedSearchTerm,
      pagingCriteria: pagingCriteria,
      ...(userId && accountId !== 1 && { userId: userId }),
    },
    skip: !companyQuestionId,
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      // Update pagination from response
      const { totalPages, currentPage, hasNextPage, hasPreviousPage, totalCount } =
        data?.getQuestionsUniqueResponse?.pageMeta || {};
      setTotalPages(totalPages);
      setCurrentPage(currentPage);
      setHasNextPage(hasNextPage);
      setHasPreviousPage(hasPreviousPage);
      setTotalCount(totalCount);
    },
  });

  // Effect to fetch form details when userForms data is available
  useEffect(() => {
    if (userForms?.getUserForms && userForms.getUserForms.length > 0) {
      const firstForm = userForms.getUserForms[0];
      setFormDetails({
        formId: firstForm.id,
        formUuid: firstForm.formUuid,
      });

      // Fetch user form details
      getUserForm({
        variables: {
          formId: firstForm.id,
          formUuid: firstForm.formUuid,
        },
      });
    }
  }, [userForms, getUserForm]);

  // Once companies list and both field IDs are available, fetch HQ for each company
  useEffect(() => {
    const companies = companiesData?.getQuestionsUniqueResponse?.data || [];
    if (!companies.length || !companyQuestionId || !hqQuestionId) return;

    const fetchAllHeadquarters = async () => {
      const newHqMap = {};
      await Promise.all(
        companies.map(async (company) => {
          const name = company.response;
          if (!name) return;
          try {
            const { data } = await client.query({
              query: GET_HEADQUARTERS,
              variables: {
                companyName: name,
                sourceId: parseInt(companyQuestionId),
                targetId: parseInt(hqQuestionId),
              },
              fetchPolicy: "network-only",
            });
            const hq = data?.getHeadquarters?.headquarters;
            if (hq) {
              newHqMap[name] = hq;
            }
          } catch (err) {
            // Silently skip if HQ not found for a company
          }
        })
      );
      setHqMap((prev) => ({ ...prev, ...newHqMap }));
    };

    fetchAllHeadquarters();
  }, [companiesData, companyQuestionId, hqQuestionId, client]);

  const handleViewDetails = (companyName) => {
    console.log("View details for:", companyName);
    // Navigate to Leads page and pass the selected company/lead name and question id
    navigate("/leads", {
      state: {
        leadName: companyName,
        questionId: companyQuestionId,
      },
    });
  };


  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPagingCriteria({
        limit: limit,
        page: newPage,
        sortByField: "created_at",
        sortOrder: "desc",
      });
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      handlePageChange(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      handlePageChange(currentPage - 1);
    }
  };

  // Pagination Component
  const PaginationBar = ({
    currentPage,
    totalPages,
    totalCount,
    limit,
    hasPreviousPage,
    hasNextPage,
    handlePreviousPage,
    handleNextPage,
    handlePageChange,
  }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 2,
        px: 2,
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {/* Left: Total Count */}
      <div className="text-sm text-gray-600">
        Showing {(currentPage - 1) * limit + 1} to{" "}
        {Math.min(currentPage * limit, totalCount)} of {totalCount} companies
      </div>

      {/* Center: Page Numbers */}
      <div className="flex items-center gap-2">
        <button
          onClick={handlePreviousPage}
          disabled={!hasPreviousPage}
          className={`px-4 py-2 rounded-md border font-medium transition-colors ${hasPreviousPage
            ? "bg-white border-[#dc3545] text-[#dc3545] hover:bg-[#dc3545] hover:text-white"
            : "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
            }`}
        >
          Prev
        </button>

        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {(() => {
            const pages = [];
            const maxVisible = 5;
            let startPage = Math.max(
              1,
              currentPage - Math.floor(maxVisible / 2)
            );
            let endPage = Math.min(totalPages, startPage + maxVisible - 1);

            if (endPage - startPage < maxVisible - 1) {
              startPage = Math.max(1, endPage - maxVisible + 1);
            }

            if (startPage > 1) {
              pages.push(
                <button key={1} onClick={() => handlePageChange(1)}>
                  1
                </button>
              );
              if (startPage > 2) {
                pages.push(<span key="ellipsis1">...</span>);
              }
            }

            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <button
                  key={i}
                  onClick={() => handlePageChange(i)}
                  className={`px-3 py-2 rounded-md border font-medium transition-colors ${currentPage === i
                    ? "bg-[#dc3545] text-white border-[#dc3545]"
                    : "border-gray-300 hover:border-[#dc3545] hover:bg-gray-50"
                    }`}
                >
                  {i}
                </button>
              );
            }

            if (endPage < totalPages) {
              if (endPage < totalPages - 1) {
                pages.push(<span key="ellipsis2">...</span>);
              }
              pages.push(
                <button
                  key={totalPages}
                  onClick={() => handlePageChange(totalPages)}
                >
                  {totalPages}
                </button>
              );
            }

            return pages;
          })()}
        </div>

        <button
          onClick={handleNextPage}
          disabled={!hasNextPage}
          className={`px-4 py-2 rounded-md border font-medium transition-colors ${hasNextPage
            ? "bg-white border-[#dc3545] text-[#dc3545] hover:bg-[#dc3545] hover:text-white"
            : "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
            }`}
        >
          Next
        </button>
      </div>

      {/* Right: Page Info */}
      <div className="text-sm text-gray-600">
        Page {currentPage} of {totalPages}
      </div>
    </Box>
  );

  // Loading state
  if (userFormsLoading || userFormLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading companies...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (companiesError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-red-500">
          <p>Error loading companies: {companiesError.message}</p>
        </div>
      </div>
    );
  }

  const companies = companiesData?.getQuestionsUniqueResponse?.data || [];
  const pageMeta = companiesData?.getQuestionsUniqueResponse?.pageMeta;

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8 mt-[80px] ml-[80px]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "16px" }}>
            <BackButton label="Back" />
            <h1 className="text-3xl font-bold text-gray-900" style={{ margin: 0 }}>Companies</h1>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-4 pr-10 py-2 border border-[#888888] rounded-[4px] focus:outline-none focus:border-[#dc3545] text-[14px]"
              />
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>

            {pageMeta && (
              <p className="text-sm text-gray-600">
                Showing {companies.length} of {pageMeta.totalCount} companies
              </p>
            )}
          </div>
        </div>

        {/* Companies Grid */}
        {companiesLoading && !companies.length ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading companies...</p>
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No companies found</h3>
            {/* <p className="mt-1 text-sm text-gray-500">
              Get started by adding your first lead.
            </p> */}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
            {companies.map((company, index) => (
              <CompanyCard
                key={`${company.response}-${index}`}
                companyName={company.response}
                totalLeads={company.totalCount}
                headquarters={hqMap[company.response] || null}
                companyDetails={company.companyDetails || null}
                roleId={sessionleadid || roleId}
                onViewDetails={() => handleViewDetails(company.response)}
                onSaveComplete={() => refetchCompanies()}
              />
            ))}
          </div>
        )}

        {/* Pagination Info */}
        <PaginationBar
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          limit={limit}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          handlePageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default Companies;